// Phase 7 D3 — Track C T-C2. Delete-account dialog.
//
// Q-3b: defense-in-depth. The Delete control is disabled until BOTH
//   (a) the user types the literal phrase "delete my account", and
//   (b) the user re-enters their current password.
// On 204 the SPA clears local auth state and redirects to /login with a
// destructive toast (Q-10 — destructive actions only).

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  ACCOUNT_DELETION_PHRASE,
  accountDeletionBodySchema,
} from '@dailydeck/shared/validation';
import type { AccountDeletionBody } from '@dailydeck/shared/types';
import { copy } from '@dailydeck/shared/copy';
import { ApiError, AuthRequiredError } from '@dailydeck/shared/api';
import { apiClient } from '../../../app/apiClient.js';
import { useAuth } from '../../../hooks/useAuth.js';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '../../../components/ui/Dialog.js';
import { Button } from '../../../components/ui/Button.js';
import { Input } from '../../../components/ui/Input.js';
import { FormError } from '../../../components/ui/FormError.js';

export function DeleteAccountDialog(): JSX.Element {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [phrase, setPhrase] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const mutation = useMutation<void, Error, AccountDeletionBody>({
    mutationFn: (body) => apiClient.auth.deleteAccount(body),
  });

  // Both gates must pass before the destructive control activates.
  const phraseOk = phrase === ACCOUNT_DELETION_PHRASE;
  const passwordOk = password.length > 0;
  const canSubmit = phraseOk && passwordOk && !mutation.isPending;

  function reset(): void {
    setPassword('');
    setPhrase('');
    setError(null);
    mutation.reset();
  }

  async function handleConfirm(): Promise<void> {
    setError(null);
    const parsed = accountDeletionBodySchema.safeParse({
      current_password: password,
      confirmation_phrase: phrase,
    });
    if (!parsed.success) {
      setError(copy.settings.account.genericErrorBody);
      return;
    }
    try {
      await mutation.mutateAsync(parsed.data);
      // 204 — local auth cleared, redirect with success state via search-param
      // so the /login route can surface the destructive toast (Q-10).
      signOut();
      navigate('/login?account_deleted=1', { replace: true });
    } catch (err) {
      if (err instanceof AuthRequiredError) {
        setError(copy.settings.account.wrongPasswordError);
        return;
      }
      if (err instanceof ApiError) {
        setError(err.message ?? copy.settings.account.genericErrorBody);
        return;
      }
      setError(copy.settings.account.genericErrorBody);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="destructive" type="button">
          {copy.settings.account.deleteAccountTrigger}
        </Button>
      </DialogTrigger>
      <DialogContent
        aria-describedby="delete-account-intro"
        className="max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{copy.settings.account.deleteDialogTitle}</DialogTitle>
          <DialogDescription id="delete-account-intro">
            {copy.settings.account.deleteDialogIntro}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="delete-account-password"
              className="text-body-strong text-text-primary"
            >
              {copy.settings.account.deleteDialogPasswordLabel}
              <span aria-hidden="true" className="ml-1 text-state-danger">
                *
              </span>
            </label>
            <Input
              id="delete-account-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-required="true"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="delete-account-phrase"
              className="text-body-strong text-text-primary"
            >
              {copy.settings.account.deleteDialogPhraseLabel}
              <span aria-hidden="true" className="ml-1 text-state-danger">
                *
              </span>
            </label>
            <Input
              id="delete-account-phrase"
              type="text"
              autoComplete="off"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={ACCOUNT_DELETION_PHRASE}
              aria-required="true"
              aria-describedby="delete-account-phrase-help"
            />
            <p
              id="delete-account-phrase-help"
              className="text-caption text-text-muted"
            >
              {copy.settings.account.deleteDialogPhraseHelp}
            </p>
          </div>
          {error !== null && <FormError>{error}</FormError>}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" type="button">
              {copy.settings.account.deleteDialogCancel}
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            type="button"
            disabled={!canSubmit}
            aria-busy={mutation.isPending}
            onClick={() => {
              void handleConfirm();
            }}
          >
            {copy.settings.account.deleteDialogConfirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
